import { injectable } from 'tsyringe';
import type { BedrockModelInfo, BillingIntegrationConfig, BillingIntegrationUpdate, ModelSettings } from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { OperatorSettingsService } from './OperatorSettingsService';

@injectable()
export class OperatorSettingsViewModel extends BaseViewModel {
    models: ModelSettings | null = null;
    modelCatalog: BedrockModelInfo[] = [];
    billing: BillingIntegrationConfig | null = null;
    loading = false;
    saving = false;
    error: string | null = null;

    constructor(private readonly service: OperatorSettingsService) {
        super();
    }

    async loadModels(): Promise<void> {
        await this.load(async () => {
            const [models, catalog] = await Promise.all([this.service.getModels(), this.service.getModelCatalog()]);
            this.models = models;
            this.modelCatalog = catalog;
        });
    }

    async saveModels(models: ModelSettings): Promise<void> {
        await this.save(() => this.service.saveModels(models), () => { this.models = models; });
    }

    async loadBilling(): Promise<void> {
        await this.load(async () => { this.billing = await this.service.getBillingIntegration(); });
    }

    async saveBilling(update: BillingIntegrationUpdate): Promise<void> {
        await this.save(() => this.service.saveBillingIntegration(update), () => { void this.loadBilling(); });
    }

    private async load(fn: () => Promise<void>): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            await fn();
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to load settings';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    private async save(fn: () => Promise<void>, onOk: () => void): Promise<void> {
        this.saving = true;
        this.notifyChange();
        try {
            await fn();
            onOk();
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }
}
